import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Button, Notice, Text } from "@smooth/design-system-components"
import { DunningDialog } from "../../components/DunningDialog"
import { evaluationError } from "../errors"
import { isUnsetTerminal } from "../../terminals"
import { dateLabel, isArchived } from "../types"
import type { EvaluationCase } from "../types"
import { inReleaseCheck } from "../origin"
import { HEADER_BUTTON_CLASS } from "./CaseFrame"
import { AlertMessage } from "./parts"

type Action = "archive" | "restore" | "remove" | "include" | "exclude"

type Props = {
  record: EvaluationCase
  /** 生成の件数（生成履歴を読み込むまでは null）。生成済みのケースは、生成結果ごと削除する旨を確認で伝える */
  runCount: number | null
  busy: boolean
  onArchive: (archived: boolean) => Promise<void>
  onRemove: () => Promise<void>
  /** 回帰テストに入れる（true）／外す（false）。渡したとき（編集中でないとき）だけメニューに出す */
  onRegression?: (included: boolean) => Promise<void>
}

const CONFIRM: Record<Action, { title: string; body: string | null; label: string }> = {
  archive: {
    title: "このケースをしまいますか？",
    body: "一覧・プロンプトの比較の対象・ケース件数から外れます。生成結果と過去のプロンプトの比較の結果は残り、「しまったケースも表示」からいつでも戻せます。",
    label: "しまう",
  },
  restore: {
    title: "このケースを戻しますか？",
    body: "一覧・プロンプトの比較の対象・ケース件数に戻ります。",
    label: "戻す",
  },
  remove: {
    title: "このケースを削除しますか？",
    body: "ケースを完全に削除します。削除すると元に戻せません。",
    label: "削除する",
  },
  include: { title: "このケースを回帰テストに入れますか？", body: null, label: "入れる" },
  exclude: { title: "このケースを回帰テストから外しますか？", body: null, label: "外す" },
}

/** 削除の確認文。生成済みなら、生成結果とプロンプトの比較の中のこのケースの結果も消えることを伝える */
const removeBody = (runCount: number | null) =>
  runCount
    ? `ケースと、その生成結果（${runCount}件。入力時点のケースの写し・プロンプト・本文）、プロンプトの比較の中のこのケースの結果を完全に削除します。削除すると元に戻せません。`
    : CONFIRM.remove.body

/** 削除できない理由（削除できるなら null） */
const removeBlocker = (record: EvaluationCase, runCount: number | null) => {
  if (record.standard) return "標準ケースは削除できません（しまうだけ）"
  if (runCount == null) return "生成履歴を読み込んでいます"
  return null
}

/**
 * ケースの「…」メニュー（回帰テストに入れる／外す・しまう・削除）、しまったケースの知らせ（戻す）、確認ダイアログを返す。どの操作も確認してから行う。
 * 普段使わない操作なので、画面にはボタン1つだけを置く（しまったケースを開いたときだけ知らせに「戻す」を出す）
 */
export function useCaseActions({
  record,
  runCount,
  busy,
  onArchive,
  onRemove,
  onRegression,
}: Props): {
  menu: ReactNode
  notice: ReactNode
  dialog: ReactNode
} {
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState<Action | null>(null)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const archived = isArchived(record)
  const blocker = removeBlocker(record, runCount)

  // メニューの外をクリック・Esc で閉じる
  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", close)
    document.addEventListener("keydown", escape)
    return () => {
      document.removeEventListener("mousedown", close)
      document.removeEventListener("keydown", escape)
    }
  }, [open])

  const ask = (action: Action) => {
    setOpen(false)
    setError(null)
    setConfirming(action)
  }
  const confirm = async () => {
    if (!confirming) return
    setWorking(true)
    setError(null)
    try {
      if (confirming === "remove") await onRemove()
      else if (confirming === "include" || confirming === "exclude")
        await onRegression?.(confirming === "include")
      else await onArchive(confirming === "archive")
      setConfirming(null)
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setWorking(false)
    }
  }

  const itemClass =
    "block w-full px-4 py-2.5 text-left text-body text-text-default hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:bg-transparent"

  const menu = (
    <div ref={menuRef} className="relative">
      <Button
        variant="outline"
        className={HEADER_BUTTON_CLASS}
        aria-label="ケースのその他の操作"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy || working}
        onClick={() => setOpen((value) => !value)}
      >
        …
      </Button>
      {open && (
        <div
          role="menu"
          aria-label="ケースのその他の操作"
          className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border border-border-default bg-bg-default py-1 shadow-card"
        >
          {!archived &&
            onRegression &&
            (inReleaseCheck(record) || !isUnsetTerminal(record.expectedTerminal)) && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => ask(inReleaseCheck(record) ? "exclude" : "include")}
              >
                {inReleaseCheck(record) ? "回帰テストから外す" : "回帰テストに入れる"}
              </button>
            )}
          {!archived && (
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => ask("archive")}
            >
              しまう
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className={itemClass}
            disabled={!!blocker}
            onClick={() => ask("remove")}
          >
            削除
            {blocker && <span className="block text-caption text-text-muted">{blocker}</span>}
          </button>
        </div>
      )}
    </div>
  )
  const dialog = confirming && (
    <DunningDialog
      title={CONFIRM[confirming].title}
      description={record.title}
      onClose={() => !working && setConfirming(null)}
      footer={
        <>
          <Button variant="outline" disabled={working} onClick={() => setConfirming(null)}>
            キャンセル
          </Button>
          <Button
            variant={confirming === "remove" ? "danger" : "primary"}
            disabled={working}
            onClick={confirm}
          >
            {CONFIRM[confirming].label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        {confirming === "remove" ? (
          <Text>{removeBody(runCount)}</Text>
        ) : (
          CONFIRM[confirming].body && <Text>{CONFIRM[confirming].body}</Text>
        )}
        {error && <AlertMessage>{error}</AlertMessage>}
      </div>
    </DunningDialog>
  )
  const notice = archived && (
    <ArchivedNotice record={record} busy={busy || working} onRestore={() => ask("restore")} />
  )
  return { menu, notice, dialog }
}

/** しまったケースを開いたときの知らせ。戻すボタンをここに置く */
function ArchivedNotice({
  record,
  busy,
  onRestore,
}: {
  record: EvaluationCase
  busy: boolean
  onRestore: () => void
}) {
  return (
    <div role="note" aria-label="しまったケース" className="mb-3">
      <Notice tone="info" variant="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text size="sm">
            しまったケースです（{record.archivedAt ? dateLabel(record.archivedAt) : ""}
            ）。一覧・プロンプトの比較の対象から外れています。生成結果は残っています。
          </Text>
          <Button variant="outline" disabled={busy} onClick={onRestore}>
            戻す
          </Button>
        </div>
      </Notice>
    </div>
  )
}
